import { useState, type ReactNode } from 'react'

import { Checklist } from '@/components/material/Checklist'
import { FieldTable } from '@/components/material/FieldTable'
import { SourceList } from '@/components/material/SourceList'
import { Badge } from '@/components/ui/Badge'
import type { MaterialQueryResponse } from '@/types/material'

const SUFFICIENCY: Record<MaterialQueryResponse['sufficiency']['level'], { label: string; tone: 'ok' | 'warn' | 'danger' }> = {
  sufficient: { label: '取料充分', tone: 'ok' },
  insufficient: { label: '取料不充分', tone: 'warn' },
  empty: { label: '取料为空', tone: 'danger' },
}

function Section({ title, extra, children }: { title: string; extra?: string; children: ReactNode }) {
  return (
    <section className="border-b border-line/70 last:border-b-0">
      <header className="flex items-baseline justify-between gap-2 px-3 pt-3">
        <h3 className="text-[13px] font-semibold text-brand-900">{title}</h3>
        {extra && <span className="text-[11px] text-muted">{extra}</span>}
      </header>
      <div className="pb-3 pt-1.5">{children}</div>
    </section>
  )
}

export function MaterialResult({ data }: { data: MaterialQueryResponse }) {
  const { anchors, sufficiency, result, sources } = data
  const [showIsolated, setShowIsolated] = useState(false)
  const level = SUFFICIENCY[sufficiency.level]
  const titleOf = (id: string) => sources.find((s) => s.id === id)?.title ?? id

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <Section title="判定概要">
        <div className="flex flex-wrap items-center gap-1.5 px-3">
          <Badge tone="brand">品类：{result.category.name ?? '未识别'}</Badge>
          {!result.category.confident && <Badge tone="warn">品类未确认（取自来源标题）</Badge>}
          <Badge tone={level.tone}>
            {level.label} · must {sufficiency.mustCoverage[0]}/{sufficiency.mustCoverage[1]}
          </Badge>
          <Badge tone={sufficiency.modelAnchored ? 'ok' : 'danger'}>
            {sufficiency.modelAnchored ? '来源已锚定型号' : '未锚定型号'}
          </Badge>
          <Badge tone={sufficiency.hasTopGrade ? 'ok' : 'warn'}>
            {sufficiency.hasTopGrade ? '有高等级来源' : '无 L0/L1/L2 来源'}
          </Badge>
          <span className="text-[11px] text-muted">
            规则 {result.audit.rulesVersion} · {result.audit.elapsedMs}ms · {result.audit.evidenceSource}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5 px-3">
          {anchors.model && <Badge>型号 {anchors.model.value}</Badge>}
          {anchors.name && <Badge>品名 {anchors.name.value}</Badge>}
          {anchors.brand && <Badge>品牌 {anchors.brand.value}</Badge>}
          {anchors.specs.map((spec) => (
            <Badge key={`${spec.value}-${spec.start}`}>规格 {spec.value}</Badge>
          ))}
          {anchors.integrityFlags.map((flag) => (
            <Badge key={flag} tone="warn">
              {flag}
            </Badge>
          ))}
        </div>
      </Section>

      <Section title="标准描述" extra={result.standardDescription.text ? '每个片段都能回链来源' : undefined}>
        <div className="px-3">
          {result.standardDescription.text ? (
            <>
              <p className="rounded-control border border-line bg-surface-2 px-3 py-2 text-sm font-medium leading-relaxed text-ink">
                {result.standardDescription.text}
              </p>
              <ul className="mt-1.5 flex flex-col gap-0.5">
                {result.standardDescription.spans.map((span, i) => (
                  <li key={`${span.sourceId}-${i}`} className="text-[11px] text-muted">
                    「{span.text}」←《{titleOf(span.sourceId)}》
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="rounded-control border border-line bg-surface-2 px-3 py-2 text-[13px] leading-relaxed text-muted">
              料不足，未成稿：没有任何字段达到"已核实/料已证"，按下方清单补料或拍板后再出描述（不编造参数）。
            </p>
          )}
        </div>
      </Section>

      {result.conflicts.length > 0 && (
        <Section title="冲突（用户值未被替换）">
          <ul className="flex flex-col gap-2 px-3">
            {result.conflicts.map((conflict) => (
              <li key={conflict.field} className="rounded-control border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] leading-relaxed">
                <p className="font-medium text-danger">
                  {conflict.field}：用户值「{conflict.userValue ?? '—'}」 vs 来源值「{conflict.recommendedValue}」
                </p>
                <p className="text-ink">{conflict.reason}</p>
                <p className="mt-0.5 text-muted">
                  双方依据：
                  {conflict.sides.map((side, i) => (
                    <span key={`${side.sourceId}-${i}`} className="mr-2">
                      {side.value}（{side.sourceId === 'user' ? '用户原话' : `《${titleOf(side.sourceId)}》 ${side.grade}`}）
                    </span>
                  ))}
                </p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="字段判定" extra={`${result.fields.length} 项 · 点击"候选"看证据与四道门`}>
        <FieldTable fields={result.fields} sources={sources} />
      </Section>

      <Section title="三级清单">
        <Checklist checklist={result.checklist} />
      </Section>

      {result.otherModelValues.length > 0 && (
        <Section title="他型号参数隔离" extra={`${result.otherModelValues.length} 条`}>
          <div className="px-3">
            <button
              type="button"
              onClick={() => setShowIsolated((v) => !v)}
              className="rounded-control px-2 py-1 text-[12px] text-brand-600 transition-colors hover:bg-brand-50"
            >
              {showIsolated ? '收起' : '展开查看被隔离的值'}
            </button>
            {showIsolated && (
              <ul className="mt-1.5 flex flex-col gap-1">
                {result.otherModelValues.map((item, i) => (
                  <li key={`${item.sourceId}-${item.field}-${i}`} className="text-[12px] leading-relaxed text-muted">
                    <span className="text-ink">
                      {item.field}={item.value}
                    </span>{' '}
                    · {item.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Section>
      )}

      <Section title="证据来源" extra={`${sources.length} 条 · 转载/镜像不重复计数`}>
        <SourceList sources={sources} />
        {sufficiency.triedQueries.length > 0 && (
          <p className="px-3 pt-2 text-[11px] leading-relaxed text-muted">
            已试查询：{sufficiency.triedQueries.join(' | ')}
          </p>
        )}
      </Section>
    </div>
  )
}
